/*
 * Copyright (c) 2025.
 * The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/solid";
import TablePageSelector from "pages/Front/components/Tanstack/components/Actions/TablePageSelector";
import TablePageSize from "pages/Front/components/Tanstack/components/Actions/TablePageSize";
import UiButton, {UiButtonSize, UiButtonStyle} from "../../Ui/components/UiButton";

interface TableFootProps {
    colSpan?: number;
    tableInit: any;
}

const TableFoot = (props: TableFootProps) => {
    const {t} = useTranslation();
    return (
        <tfoot className={"TableFoot sticky bottom-0 bg-white drop-shadow select-none"}>
            <tr className={"h-[64px]"}>
                <td className={"px-3.5 py-3"} colSpan={props.colSpan ? props.colSpan : 10}>
                    <div className={"grid grid-cols-3 justify-between items-center"}>
                        <div className="col-span-1 flex flex-col items-start mr-4 w-auto">
                            <div className={"flex flex-row items-center"}>
                                <span className={"mr-1.5"}>{t("Page")}</span>
                                <TablePageSelector
                                    disabled={props.tableInit.isLoading}
                                    pageCount={props.tableInit.table.getPageCount() ? props.tableInit.table.getPageCount() : 0}
                                    selected={props.tableInit.table.getPageCount() === 0 ? 0 : props.tableInit.table.getState().pagination.pageIndex + 1}
                                    onChange={props.tableInit.paginationSetPageIndexHandler}
                                />
                                <strong>
                                    {t("of")} {props.tableInit.table.getPageCount() ? props.tableInit.table.getPageCount() : "---"}
                                </strong>
                            </div>
                            <div>
                                {t("Number of records")}: <strong>{props.tableInit.recordsCount ? props.tableInit.recordsCount : "---"}</strong>
                            </div>
                        </div>
                        <div className="col-span-2 flex flex-row w-full justify-end gap-2">
                            <TablePageSize
                                disabled={props.tableInit.isLoading}
                                pageSizes={[5, 10, 20, 50, 100]}
                                selected={props.tableInit.table.getState().pagination.pageSize}
                                onChange={props.tableInit.paginationSetPageSize}
                            />
                            <UiButton
                                disabled={!props.tableInit.table.getCanPreviousPage() || props.tableInit.isLoading}
                                icon={<ChevronDoubleLeftIcon className={"w-6 h-6"} />}
                                size={UiButtonSize.Pagination}
                                style={UiButtonStyle.Pagination}
                                onClick={() => props.tableInit.paginationSetPageIndexHandler(0)}
                            />
                            <UiButton
                                disabled={!props.tableInit.table.getCanPreviousPage() || props.tableInit.isLoading}
                                icon={<ChevronLeftIcon className={"w-6 h-6"} />}
                                size={UiButtonSize.Pagination}
                                style={UiButtonStyle.Pagination}
                                onClick={() => props.tableInit.paginationPreviousPageHandler()}
                            />
                            <UiButton
                                disabled={!props.tableInit.table.getCanNextPage() || props.tableInit.isLoading}
                                icon={<ChevronRightIcon className={"w-6 h-6"} />}
                                size={UiButtonSize.Pagination}
                                style={UiButtonStyle.Pagination}
                                onClick={() => props.tableInit.paginationNextPageHandler()}
                            />
                            <UiButton
                                disabled={!props.tableInit.table.getCanNextPage() || props.tableInit.isLoading}
                                icon={<ChevronDoubleRightIcon className={"w-6 h-6"} />}
                                size={UiButtonSize.Pagination}
                                style={UiButtonStyle.Pagination}
                                onClick={() => props.tableInit.paginationSetPageIndexHandler(props.tableInit.table.getPageCount() - 1)}
                            />
                        </div>
                    </div>
                </td>
            </tr>
        </tfoot>
    );
};

export default TableFoot;
